import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Area, AreaChart, Tooltip } from 'recharts';

const ThreatChart = ({ data = null, loading = false, year = new Date().getFullYear() }) => {
  // Sample data if no data provided
  const defaultData = [
    { month: 'Jan', threats: 150 },
    { month: 'Feb', threats: 220 },
    { month: 'Mar', threats: 180 },
    { month: 'Apr', threats: 300 },
    { month: 'May', threats: 250 },
    { month: 'Jun', threats: 270 },
    { month: 'Jul', threats: 320 },
    { month: 'Aug', threats: 200 },
    { month: 'Sep', threats: 230 },
    { month: 'Oct', threats: 280 },
    { month: 'Nov', threats: 260 },
    { month: 'Dec', threats: 350 }
  ];

  const formatApiData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return defaultData;
    return apiData.map(item => ({
      month: new Date(0, item.month - 1).toLocaleString('en', { month: 'short' }),
      threats: item.scanCount || 0
    }));
  };

  const chartData = data ? formatApiData(data) : defaultData;

  if (loading) {
    return (
      <div className="threat-chart-container" style={{ 
        background: 'white', 
        borderRadius: '12px', 
        padding: '24px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #f3f4f6', 
            borderTop: '4px solid #3b82f6', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6b7280', margin: 0 }}>Loading chart data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="threat-chart-container" style={{ 
      background: 'white', 
      borderRadius: '12px', 
      padding: '24px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb'
    }}>
      <div className="chart-header" style={{ marginBottom: '24px' }}>
        <h3 style={{ 
          fontSize: '18px', 
          fontWeight: '600', 
          color: '#374151',
          margin: '0 0 8px 0'
        }}>
          Monthly Cybersecurity Threat Detections in {year}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ 
            width: '12px', 
            height: '12px', 
            backgroundColor: '#3b82f6', 
            borderRadius: '50%' 
          }}></div>
          <span style={{ fontSize: '14px', color: '#6b7280' }}>
            Cybersecurity Threats Detected ({year})
          </span>
        </div>
      </div>
      
      <div style={{ width: '100%', height: '300px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorThreats" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis 
              dataKey="month" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              label={{ 
                value: 'Number of Threats Detected', 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle', fontSize: '12px', fill: '#6b7280' }
              }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
              labelStyle={{ color: '#374151', fontWeight: '600' }}
              formatter={(value, name) => [
                `${value} scans`,
                'Scan Count'
              ]}
              labelFormatter={(label) => `Month: ${label}`}
            />
            <Area
              type="monotone"
              dataKey="threats"
              stroke="#3b82f6"
              strokeWidth={3}
              fill="url(#colorThreats)"
              dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4, cursor: 'pointer' }}
              activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2, fill: 'white', cursor: 'pointer' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ThreatChart;